import { T } from 'gt-react';
import { PlusIcon } from '@phosphor-icons/react';
import type { UploadLayout, UploadRule } from '@erp/rpc';

import { Button } from '@/components/ui/button';
import { RuleDialog } from './RuleDialog';
import { useRuleEditing } from './ruleEditing';
import { RuleEditor } from './RuleEditor';
import { EMPTY_RULE_FORM, formFromRule, type RuleFormState } from './ruleForm';
import { ToolCard } from './ToolCard';
import { UnreadableRules, UploadRuleRow } from './UploadRuleRow';
import { usePricingPermissions } from './usePricingPermissions';
import { useUploadRuleMutations } from './useUploadRuleMutations';

interface Props {
  rules: UploadRule[];
  layouts: UploadLayout[];
  /** Stored rules this version can't read; listed so they can be deleted. */
  unreadableRuleIds: string[];
  isLoading: boolean;
  isError: boolean;
}

function PanelMessage({
  isError,
  isEmpty,
}: {
  isError: boolean;
  isEmpty: boolean;
}) {
  if (isError) {
    return (
      <p className="text-destructive text-sm">
        <T context="upload rules panel error when the rules cannot be loaded">
          The validation rules could not be loaded, so downloads are paused.
          Reload the page to try again.
        </T>
      </p>
    );
  }
  if (!isEmpty) return null;
  return (
    <p className="text-muted-foreground text-sm">
      <T context="empty state of the upload validation rules panel">
        No rules yet.
      </T>
    </p>
  );
}

/**
 * The org's upload validation rules in a compact card. Each change is saved
 * straight away and re-checks the file already on the page; adding or editing
 * a rule opens a dialog.
 */
export function UploadRulesPanel({
  rules,
  layouts,
  unreadableRuleIds,
  isLoading,
  isError,
}: Props) {
  const { canCreate, canUpdate, canDelete } = usePricingPermissions();
  const { create, update, remove } = useUploadRuleMutations();
  const { editing, open, close, save, rowActions } = useRuleEditing<
    UploadRule,
    RuleFormState
  >({
    canUpdate,
    canDelete,
    formFromRule,
    toggle: ({ id }, enabled) => update.mutate({ id, enabled }),
    remove: ({ id }) => remove.mutate({ id }),
  });
  const isEmpty =
    rules.length === 0 && unreadableRuleIds.length === 0 && !isLoading;

  function saveRule(
    rule: Parameters<Parameters<typeof RuleEditor>[0]['onSave']>[0],
  ) {
    save(rule, {
      update: (id, draft, onSuccess) =>
        update.mutate({ id, ...draft }, { onSuccess }),
      create: (draft, onSuccess) =>
        create.mutate({ ...draft, enabled: true }, { onSuccess }),
    });
  }

  return (
    <ToolCard
      title={
        <T context="heading of the upload validation rules card">
          Validation Rules
        </T>
      }
      description={
        <T context="explanation under the upload validation rules heading">
          Checked on every upload line. Block stops the download; Warn asks
          first. Blank values only fail “Is required”.
        </T>
      }
      action={
        canCreate ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => open(null, EMPTY_RULE_FORM)}
          >
            <PlusIcon className="h-4 w-4" />
            <T context="button that adds an upload validation rule">Add Rule</T>
          </Button>
        ) : null
      }
    >
      <ul>
        {rules.map((rule) => (
          <UploadRuleRow
            key={rule.id}
            rule={rule}
            layouts={layouts}
            isBusy={update.isPending || remove.isPending}
            {...rowActions(rule)}
          />
        ))}
      </ul>
      <PanelMessage isError={isError} isEmpty={isEmpty && !isError} />
      <UnreadableRules
        ids={unreadableRuleIds}
        isBusy={remove.isPending}
        onDelete={canDelete ? (id) => remove.mutate({ id }) : null}
      />

      <RuleDialog
        isOpen={editing.mode === 'open'}
        onClose={close}
        className="max-w-3xl"
        title={
          <T context="title of the upload validation rule dialog">
            Validation Rule
          </T>
        }
      >
        {editing.mode === 'open' ? (
          <RuleEditor
            key={editing.ruleId ?? 'new'}
            initial={editing.form}
            layouts={layouts}
            isSaving={create.isPending || update.isPending}
            onCancel={close}
            onSave={saveRule}
          />
        ) : null}
      </RuleDialog>
    </ToolCard>
  );
}
